<script setup lang="ts">

import { type EcommerceItem } from '@/types/shared/player-config'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import BaseButton from '@/components/base/BaseButton.vue'
import ShopCard2 from '@/components/ShopCard2.vue'
import ShopCard from '@/components/ShopCard.vue'
import { ref } from 'vue'
import { useConfetti } from '@/composables/game/confetti'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import PaymentButtons from '@/components/PaymentButtons.vue'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import { T } from '@tolgee/vue'

const props = withDefaults(defineProps<{
	item: EcommerceItem
	image?: string
	coinsToEarn?: number
	spinsToEarn?: number
	cardStyle?: 'default' | '2'
	cardOuterStyle?: string
	showName?: boolean
	hideAfterPurchase?: boolean
	tonDiscountBadgeClass?: string
}>(), {
  cardStyle: 'default',
  coinsToEarn: undefined,
  spinsToEarn: undefined,
  image: undefined,
  cardOuterStyle: undefined,
  showName: false,
  hideAfterPurchase: true,
  tonDiscountBadgeClass: '',
})

const emit = defineEmits<{
	(e: 'hide', result: { paid: boolean, nonce?: string }): void
}>()

const { getStarPrice } = useEcommercePrice()
const { showConfetti } = useConfetti()

const starPrice = getStarPrice(props.item)
const purchaseDone = ref(false)
const purchaseResult = ref<{ paid: boolean, nonce?: string }>({ paid: false })
const coins = props.coinsToEarn

function handlePurchase(result: { paid: boolean, nonce?: string }) {
  if (result.paid) {
    purchaseDone.value = true
    purchaseResult.value = result
    if (props.hideAfterPurchase) {
      emit('hide', result)
    } else {
      showConfetti()
    }
  }
}
</script>

<template>
  <div class="w-full flex flex-col items-center">
    <img
      v-if="image"
      class="h-[120px] w-auto object-cover"
      :src="image"
    >
    <div
      v-else
      class="relative h-[117px] w-auto"
    >
      <ShopCard
        v-if="cardStyle === 'default'"
        :item="item"
        only-visual
        :style="cardOuterStyle"
      />
      <ShopCard2
        v-else
        :item="item"
        only-visual
        :style="cardOuterStyle"
      />
    </div>
    <div
      v-if="coins"
      class="mt-6 flex items-center gap-x-3"
      v-tooltip="{content: formatUnshortenedNumber(coins), placement: 'bottom'}"
    >
      <img
        :src="CoinIcon"
        class="h-10 w-auto min-w-[26px]"
      >
      <div class="text-light-white text-[32px] leading-10 font-bold">
        {{ formatNumberFullTruncate(coins) }}
      </div>
    </div>
    <p
      v-if="props.spinsToEarn"
      class="mt-2 text-light-white text-2xl font-bold"
    >
      <T
        key-name="fortune_wheel.info.more_spins.count_spin"
        default-value="{count, plural, one {# spin} other {# spins}}"
        :params="{count: props.spinsToEarn}"
      />
    </p>
    <template v-if="showName || !hideAfterPurchase">
      <template v-if="!hideAfterPurchase && purchaseDone">
        <div
          class="mt-6 flex text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]"
        >
          Purchase successful!
        </div>
        <div class="w-full text-center text-white/60 text-base font-medium">
          {{ item.name }} = ${{ formatNumberFullTruncate(starPrice?.usdAmount, true) }}
        </div>
      </template>
      <div
        v-else-if="showName"
        class="mt-3 text-center flex text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]"
      >
        <span>
          {{ item.name }} = ${{ formatNumberFullTruncate(starPrice?.usdAmount, true) }}
        </span>
      </div>
    </template>

    <div
      v-if="hideAfterPurchase || !purchaseDone"
      class="mt-6 w-full"
    >
      <PaymentButtons
        :item="item"
        @purchase="handlePurchase"
      />
    </div>

    <BaseButton
      v-else-if="!hideAfterPurchase && purchaseDone"
      style-type="secondary"
      class="mt-2 w-full"
      @click="emit('hide', purchaseResult)"
    >
      Close
    </BaseButton>
  </div>
</template>

<style scoped>

</style>